// Purchaser identity stays in event properties; the main app owns person identification.
// PostHog boots from the pt_did identity cookie, and asks the API to set it once loaded when it is missing.
import posthog from "posthog-js"
import { PostHogHost, PostHogKey } from "../../config"
import { type OfferingProduct } from "../../features/payment/domain/models/offering"
import { ensureIdentityCookie, readIdentityCookie } from "../helpers/identity-cookie"

export type TrackingVariant = "root" | "peak"

/** Who paid, as Paddle reports them on checkout completion. */
export interface PurchaserDetails {
  readonly email?: string
  readonly paddleCustomerId?: string
}

function toPurchaserPayload(purchaser: PurchaserDetails = {}): object {
  return {
    ...(purchaser.email ? { email: purchaser.email } : {}),
    ...(purchaser.paddleCustomerId ? { paddle_customer_id: purchaser.paddleCustomerId } : {}),
  }
}

const PATHNAME_VARIANT_MAP: Partial<Record<string, TrackingVariant>> = {
  "/peak": "peak",
}

function variantForPathname(pathname: string): TrackingVariant {
  const normalised = pathname.replace(/\/+$/, "") || "/"
  return PATHNAME_VARIANT_MAP[normalised] ?? "root"
}

function toOfferingPayload(offering: OfferingProduct): object {
  return {
    id: offering.id,
    name: offering.name,
    price: offering.price,
    currency_code: offering.currencyCode,
    offering_type: String(offering.offeringType),
    price_type: offering.priceType,
    billing_interval: offering.billingInterval ? String(offering.billingInterval.interval) : undefined,
    billing_frequency: offering.billingInterval ? Number(offering.billingInterval.frequency) : undefined,
  }
}

export interface PostHogIdentity {
  readonly distinctId?: string
  readonly sessionId?: string
}

/** The visitor's PostHog ids, or an empty object when PostHog cannot answer. */
export const getPostHogIdentity = (): PostHogIdentity => {
  try {
    return { distinctId: posthog.get_distinct_id(), sessionId: posthog.get_session_id() }
  } catch {
    return {}
  }
}

export interface TrackingService {
  setVariant: (variant: TrackingVariant) => void
  trackEvent: (event: string, properties?: object) => void
  trackOfferCtaClicked: (offering: OfferingProduct | undefined) => void
  trackDisclaimerConfirmed: () => void
  trackCheckoutOpened: (offering: OfferingProduct) => void
  trackCheckoutAbandoned: (offering: OfferingProduct) => void
  trackCheckoutPaymentFailed: (offering: OfferingProduct, reason: string) => void
  trackCheckoutCompleted: (offering: OfferingProduct, purchaser?: PurchaserDetails) => void
  trackSignupCtaClicked: () => void
}

export class PostHogService implements TrackingService {
  private variant: TrackingVariant = "root"

  constructor() {
    const did = readIdentityCookie()
    posthog.init(PostHogKey, {
      api_host: PostHogHost,
      ...(did ? { bootstrap: { distinctID: did } } : {}),
      loaded: (client) => {
        if (!did) void ensureIdentityCookie(client.get_distinct_id())
      },
      before_send: (event) => {
        if (event?.event === "$pageview") {
          return {
            ...event,
            properties: {
              ...event.properties,
              variant: variantForPathname(window.location.pathname),
            },
          }
        }
        return event
      },
    })
  }

  public setVariant = (variant: TrackingVariant): void => {
    this.variant = variant
    posthog.register({ variant })
  }

  public trackEvent(event: string, properties?: object): void {
    try {
      posthog.capture(event, { ...properties, variant: this.variant })
    } catch (error) {
      console.error("PostHog track error:", error)
    }
  }

  public trackOfferCtaClicked = (offering: OfferingProduct | undefined): void => {
    this.trackEvent("offer_cta_clicked", offering ? { offering: toOfferingPayload(offering) } : undefined)
  }

  public trackDisclaimerConfirmed = (): void => {
    this.trackEvent("disclaimer_confirmed")
  }

  public trackCheckoutOpened = (offering: OfferingProduct): void => {
    this.trackEvent("paddle_checkout_started", { offering: toOfferingPayload(offering) })
  }

  public trackCheckoutAbandoned = (offering: OfferingProduct): void => {
    this.trackEvent("paddle_checkout_cancelled", { offering: toOfferingPayload(offering) })
  }

  public trackCheckoutPaymentFailed = (offering: OfferingProduct, reason: string): void => {
    this.trackEvent("paddle_checkout_failed", { offering: toOfferingPayload(offering), reason })
  }

  public trackCheckoutCompleted = (offering: OfferingProduct, purchaser?: PurchaserDetails): void => {
    this.trackEvent("paddle_checkout_completed", {
      offering: toOfferingPayload(offering),
      ...toPurchaserPayload(purchaser),
    })
  }

  public trackSignupCtaClicked = (): void => {
    this.trackEvent("signup_cta_clicked")
  }
}
